import React, {
  createContext, useCallback,
  useContext, useEffect,
  useState,
} from "react";
import { IdString, ITributeNotification } from "../models/others";
import { getTributeNotifications } from "../api/others";
import { useAppSelector } from "../store";
import { useStoryDataContext } from "./StoryContext";


type TributeNotificationsContextType = {
  tributeNotifications: ITributeNotification[];
  isLoading: boolean;
  markListRead: (uuids: IdString[]) => void;
};

const TributeNotificationsDataContext = createContext<TributeNotificationsContextType | undefined>(
  undefined
);

export const TributeNotificationsDataProvider = ({children}: {
  children: React.ReactNode;
}) => {
  const { story} = useStoryDataContext();
  const {profile} = useAppSelector((state) => state.profile);
  const [tributeNotifications, setTributeNotifications] = useState<ITributeNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!profile) return;
    setIsLoading(true);

    try {
      const res = await getTributeNotifications();
      setTributeNotifications(res);
    } catch (error) {
      //
    } finally {
      setIsLoading(false);
    }
  }, [profile, story?.uuid]);

  useEffect(() => {
    loadData()
  }, [loadData]);

  const markListRead = (uuids: IdString[]) => {
    setTributeNotifications(p => {
      return p.map(n => uuids.includes(n.uuid) ? { ...n, is_read: true } : n);
    });
  }

  return (
    <TributeNotificationsDataContext.Provider value={{
      tributeNotifications, isLoading, markListRead
    }}>
      {children}
    </TributeNotificationsDataContext.Provider>
  );
};

export const useTributeNotificationsDataContext = () => {
  const context = useContext(TributeNotificationsDataContext);
  if (!context) {
    throw new Error("useTributeNotificationsDataContext must be used within a TributeNotificationsDataProvider");
  }
  return context;
};
